<script setup>
// ============================================================================
// BonBoZ: bảng "Thống kê chi tiết thẻ hội thoại" — MỘT DÒNG MỖI THẺ, MỘT CỘT
// MỖI MỐC, đúng khuôn Pancake. Bảng duy nhất trong phần Thống kê xoay ngang:
// thời gian nằm ở CỘT chứ không ở dòng.
//
// Ô TRỐNG KHÁC HẲN Ô SỐ 0. Ngày không ai đụng tới thẻ thì để trống, đúng như
// Pancake — "0" đọc ra là "gắn rồi gỡ, hoà", một chuyện hoàn toàn khác. Máy chủ
// trả 0 cho cả hai nên phân biệt tại đây bằng `so === 0 && !coMoc`… không được:
// cách duy nhất tin được là máy chủ CHỈ trả về những mốc có thay đổi, rồi ô nào
// bằng 0 trong số đó thì đúng là "hoà". Xem đầu `Bonboz::TheBuilder`.
//
// SỐ ÂM GIỮ NGUYÊN và tô đỏ: một ngày gỡ nhiều hơn gắn thì cột đó là −1. Kẹp
// về 0 là giấu mất việc dọn nhãn, và làm cột Tổng không còn cộng ra.
// ============================================================================
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ngay } from 'dashboard/helper/dinhDangNgay';
import {
  taiXuongExcel,
  tenFileThongKe,
} from 'dashboard/helper/bonboz/xuatExcel';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  the: { type: Array, default: () => [] },
  moc: { type: Array, default: () => [] },
  gom: { type: String, default: 'ngay' },
  dangTai: { type: Boolean, default: false },
});
const emit = defineEmits(['doiGom']);

const { t } = useI18n();

const tim = ref('');
// Xếp theo cột Tổng, bấm mũi tên để đảo — đúng cái nút Pancake để ở đầu cột đó.
const tangDan = ref(false);

const nhanMoc = m =>
  props.gom === 'gio'
    ? `${String(m).padStart(2, '0')}:00`
    : ngay(m * 1000, 'ngayThang');

const hang = computed(() => {
  const tu_khoa = tim.value.trim().toLowerCase();
  const ds = tu_khoa
    ? props.the.filter(d => d.ten.toLowerCase().includes(tu_khoa))
    : [...props.the];
  return ds.sort((a, b) => (tangDan.value ? a.tong - b.tong : b.tong - a.tong));
});

// Ô có giá trị 0 trong một mốc CÓ thay đổi nghĩa là "gắn rồi gỡ, hoà" — vẫn để
// trống cho giống Pancake, nhưng người đọc rê chuột vào cột là thấy ngày.
const oCua = so => (so === 0 ? '' : so);

const xuatExcel = () => {
  const cacHang = [
    [
      t('BONBOZ.THE_TK.COT_THE'),
      ...props.moc.map(nhanMoc),
      t('BONBOZ.TRANG_TK.COT_TONG'),
    ],
    ...hang.value.map(d => [d.ten, ...d.theo_moc, d.tong]),
  ];
  taiXuongExcel(
    tenFileThongKe(
      `thong-ke-the-${props.gom}`,
      props.gom === 'ngay' && props.moc.length ? Math.min(...props.moc) : null,
      props.gom === 'ngay' && props.moc.length ? Math.max(...props.moc) : null
    ),
    cacHang
  );
};

const TAB = [
  { khoa: 'ngay', nhan: 'TAB_CHI_TIET' },
  { khoa: 'gio', nhan: 'TAB_HOM_NAY' },
];
</script>

<template>
  <section class="border rounded-xl border-n-weak bg-n-solid-2">
    <div class="p-4 pb-0">
      <h3 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ $t('BONBOZ.THE_TK.CHI_TIET') }}
      </h3>
      <p class="m-0 mt-0.5 text-xs text-n-slate-11">
        {{ $t('BONBOZ.THE_TK.CHI_TIET_MO_TA') }}
      </p>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3 px-4 pt-3">
      <div class="flex gap-5 -mb-px">
        <button
          v-for="x in TAB"
          :key="x.khoa"
          type="button"
          class="pb-2 text-sm transition-colors border-b-2"
          :class="
            gom === x.khoa
              ? 'border-n-brand text-n-brand font-medium'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="emit('doiGom', x.khoa)"
        >
          {{ $t(`BONBOZ.THE_TK.${x.nhan}`) }}
        </button>
      </div>

      <div class="flex items-center gap-2 pb-2">
        <label class="relative">
          <span
            class="absolute i-lucide-search size-4 left-3 top-2.5 text-n-slate-10"
            aria-hidden="true"
          />
          <input
            v-model="tim"
            type="search"
            class="py-2 text-sm border rounded-lg ps-9 pe-3 w-60 border-n-weak bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10"
            :placeholder="$t('BONBOZ.THE_TK.TIM')"
          />
        </label>
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-download"
          :label="$t('BONBOZ.XUAT.NUT')"
          :disabled="!hang.length"
          @click="xuatExcel"
        />
      </div>
    </div>

    <div
      v-if="dangTai"
      class="m-4 rounded-lg h-48 bg-n-alpha-2 animate-pulse"
    />

    <p
      v-else-if="!hang.length"
      class="m-0 py-12 text-sm text-center text-n-slate-11"
    >
      {{ $t(tim ? 'BONBOZ.THE_TK.KHONG_KHOP' : 'BONBOZ.THE_TK.CHUA_CO') }}
    </p>

    <!-- Bảng tự cuộn ngang trong khung của nó; trang không bao giờ cuộn ngang.
         Cột Thẻ và cột Tổng dính hai đầu — đó là hai cột người đọc luôn cần
         nhìn thấy khi kéo qua ba mươi cột ngày ở giữa. -->
    <div v-else class="overflow-auto max-h-[32rem] border-t border-n-weak">
      <!-- BonBoZ: boc de cuon ngang. Bang nhieu cot hon be ngang man thi bi CAT
           cung chu khong cuon — tren dien thoai mat han cac cot ben phai. Hai man
           Cam xuc va Quang cao da boc dung tu truoc, day la lam not cho deu. -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead class="sticky top-0 z-20 bg-n-slate-2">
            <tr class="text-xs border-b border-n-weak text-n-slate-11">
              <th
                class="sticky left-0 z-10 px-3 py-2.5 font-medium w-44 bg-n-slate-2 text-start"
              >
                {{ $t('BONBOZ.THE_TK.COT_THE') }}
              </th>
              <th
                v-for="m in moc"
                :key="m"
                class="px-3 py-2.5 font-medium whitespace-nowrap text-end"
              >
                {{ nhanMoc(m) }}
              </th>
              <th
                class="sticky right-0 z-10 px-3 py-2.5 font-medium border-l bg-n-slate-2 border-n-weak text-end"
              >
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded hover:text-n-slate-12"
                  @click="tangDan = !tangDan"
                >
                  {{ $t('BONBOZ.TRANG_TK.COT_TONG') }}
                  <span
                    class="block size-3.5"
                    :class="
                      tangDan
                        ? 'i-lucide-arrow-up-narrow-wide'
                        : 'i-lucide-arrow-down-wide-narrow'
                    "
                    aria-hidden="true"
                  />
                </button>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="d in hang"
              :key="d.ten"
              class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
            >
              <td
                class="sticky left-0 px-3 py-2 whitespace-nowrap bg-n-solid-2 text-n-slate-12"
              >
                <span class="flex items-center gap-2">
                  <!-- Màu ĐÚNG như người dùng đặt ở Cài đặt → Nhãn: nhìn thẻ ở
                       đâu trong sản phẩm cũng phải ra cùng một màu. -->
                  <span
                    class="block i-lucide-tag size-4 shrink-0"
                    :style="{ color: d.mau || 'rgb(var(--bonboz-mo))' }"
                    aria-hidden="true"
                  />
                  <span class="truncate">{{ d.ten }}</span>
                </span>
              </td>
              <td
                v-for="(so, i) in d.theo_moc"
                :key="moc[i]"
                class="px-3 py-2 tabular-nums text-end"
                :class="so < 0 ? 'text-n-ruby-11' : 'text-n-slate-12'"
              >
                {{ oCua(so) }}
              </td>
              <td
                class="sticky right-0 px-3 py-2 font-semibold border-l tabular-nums bg-n-solid-2 border-n-weak text-end"
                :class="d.tong < 0 ? 'text-n-ruby-11' : 'text-n-slate-12'"
              >
                {{ d.tong }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <p class="px-4 py-2.5 m-0 text-xxs text-n-slate-11">
      {{ $t('BONBOZ.THE_TK.GT_O_TRONG') }}
    </p>
  </section>
</template>
